import React from 'react';
import { Button } from '../ui/button';
import { AlertCircle, Copy, Printer, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { escapeHtml, openPrintWindow } from '../../lib/printUtils';

/**
 * Visualizzazione codici di recupero 2FA — l'utente li copia o stampa, poi prosegue.
 * NB: questa schermata è one-shot; dopo "Procedi" i codici non sono più visibili.
 */
const RecoveryCodesDisplay = ({ codes, email, onProceed }) => {
  const copyCodes = () => {
    navigator.clipboard.writeText(codes.join('\n'));
    toast.success('Codici copiati negli appunti');
  };

  const printCodes = () => {
    const dateStr = new Date().toLocaleString('it-IT');
    const codesHtml = codes.map(c => `<div class="code">${escapeHtml(c)}</div>`).join('');
    const html = `<!DOCTYPE html><html><head><title>Codici di recupero 2FA - Semplificaffitto.it</title>
<style>
  @media print { @page { margin: 2cm; } body { -webkit-print-color-adjust: exact; } }
  body { font-family: Arial, sans-serif; max-width: 700px; margin: 40px auto; padding: 20px; color: #1e293b; }
  h1 { color: #1e40af; margin-bottom: 8px; }
  .subtitle { color: #64748b; margin-bottom: 24px; font-size: 14px; }
  .warning { background:#fef3c7; border:1px solid #fbbf24; border-radius:6px; padding:12px; margin:20px 0; font-size:13px; color:#78350f; }
  .codes { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin: 24px 0; }
  .code { font-family: 'Courier New', monospace; font-size: 22px; font-weight: bold; text-align:center; padding: 12px; background:#f1f5f9; border:1px solid #cbd5e1; border-radius:6px; letter-spacing:2px; }
  .footer { margin-top: 30px; font-size: 12px; color: #64748b; border-top: 1px solid #e2e8f0; padding-top: 16px; }
  .meta { font-size: 12px; color: #475569; margin-bottom: 16px; }
</style></head><body>
<h1>Codici di recupero 2FA</h1>
<div class="subtitle">Semplificaffitto.it &middot; by EDILSTUDIO s.r.l.</div>
<div class="meta">
  <strong>Account:</strong> ${escapeHtml(email)}<br/>
  <strong>Data emissione:</strong> ${escapeHtml(dateStr)}
</div>
<div class="warning">
  <strong>ATTENZIONE</strong> &mdash; Conserva questi 10 codici in un luogo sicuro (cassaforte, password manager, busta sigillata).
  Ogni codice può essere usato <strong>una sola volta</strong> per accedere se perdi il telefono.
  Dopo averli usati tutti, dovrai rigenerarli dall'area "Sicurezza" del tuo account.
</div>
<div class="codes">${codesHtml}</div>
<div class="footer">
  Documento confidenziale &mdash; Non condividere con terzi.<br/>
  Per assistenza: <strong>info@semplificaffitto.it</strong>
</div>
<script>window.onload = function() { setTimeout(function() { window.print(); }, 300); };</script>
</body></html>`;
    const w = openPrintWindow(html);
    if (!w) {
      toast.error('Popup bloccato dal browser. Abilita i popup per stampare.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800">
        <p className="font-semibold flex items-center gap-2 mb-2">
          <AlertCircle className="w-4 h-4" />IMPORTANTE
        </p>
        <p>
          Questi <strong>10 codici di recupero</strong> ti servono per accedere se perdi il telefono.{' '}
          <strong>Stampali o salvali subito</strong>: dopo questa schermata non li potrai più vedere.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 bg-slate-50 border rounded-md p-4 font-mono text-sm" data-testid="recovery-codes-list">
        {codes.map((c) => (
          <div key={c} className="text-center py-1 bg-white border rounded">{c}</div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={copyCodes} data-testid="copy-recovery-codes-btn">
          <Copy className="w-4 h-4 mr-2" /> Copia
        </Button>
        <Button variant="outline" onClick={printCodes} data-testid="print-recovery-codes-btn">
          <Printer className="w-4 h-4 mr-2" /> Stampa
        </Button>
      </div>

      <Button
        className="w-full bg-slate-900 hover:bg-slate-800"
        onClick={onProceed}
        data-testid="proceed-after-recovery-btn"
      >
        <CheckCircle2 className="w-4 h-4 mr-2" /> Ho salvato i codici, procedi
      </Button>
    </div>
  );
};

export default RecoveryCodesDisplay;
